// The Feedback pill: bottom right of EVERY page, and the note it opens.
//
// ⚠⚠ TEMPORARY — remove once the system is locked in (owner, 2026-09-16: "a feedback button on every page, temporarily,
// until the system is locked in"). Mounted ONCE, in `app.tsx`, so it shows on the GM workspace, the player home, the
// players' table (`/t/…`), the campaign page (`/c/…`), the player sheet (`/p/…`) and handouts — every surface except the
// sign-in pane. Take it out of `app.tsx` and delete this file, `lib/feedback.ts` and the Feedback card on the campaign
// sheet together.
//
// ⚠ THE MODAL'S STATE LIVES HERE, NOT IN THE PAGE THAT MOUNTS IT. The account menu (in the workspace header and on the
// player home) also needs to open it, and threading an `open` callback through three header literals in `workspace.tsx`
// — a file another session is editing — is the wrong coupling. So the menu row dispatches `compendium:feedback` on
// `window` and the ONE mounted instance listens. `openFeedback()` is the only way in; nothing else reaches the state.
//
// ⚠ WHERE IT SITS. Bottom right, 0.9rem from the edges — the site's `.backmap` pill is bottom CENTRE (z 140) so they
// never meet. The chat pill (`.chat-pill`, z 71) IS bottom right on the player pages, so `styles.css` lifts this one
// above it whenever a chat pill is on the page (`body:has(.chat-pill)`), and the chat's open float, which is taller than
// both, hides the pill behind it rather than fight it.
import { useCallback, useEffect, useRef, useState } from "react";
import { sendFeedback } from "../lib/feedback";
import { latestWhatsNew } from "../lib/whats-new";
import whatsNewText from "../../WHATS-NEW.md?raw";

export const FEEDBACK_EVENT = "compendium:feedback";

/** Open the feedback note from anywhere — the account menu's row, or anything else that wants it. */
export function openFeedback(): void {
  window.dispatchEvent(new CustomEvent(FEEDBACK_EVENT));
}

export function FeedbackButton({ shareToken = null, campaignId }: {
  /** On a share-link page: the token, which is all the page knows. The database turns it into the campaign (0076). */
  shareToken?: string | null;
  /**
   * Which campaign the page is working in, read WHEN THE NOTE OPENS rather than at mount: the workspace switches
   * campaigns without remounting the app, and the app shell doesn't hold the workspace's state.
   */
  campaignId?: () => string | null;
}) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [page, setPage] = useState("");
  const box = useRef<HTMLTextAreaElement>(null);

  const show = useCallback(() => {
    // The page it was pressed on: the path and the document title, as the GM will read it.
    const path = `${window.location.pathname}${window.location.search}`;
    const title = (document.title || "").trim();
    setPage(title ? `${path} · ${title}` : path);
    setNote(null);
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener(FEEDBACK_EVENT, show);
    return () => window.removeEventListener(FEEDBACK_EVENT, show);
  }, [show]);
  useEffect(() => { if (open) box.current?.focus(); }, [open]);

  const close = () => { setOpen(false); setBusy(false); };

  const send = async () => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    const ok = await sendFeedback(
      { campaignId: campaignId?.() ?? null, shareToken, page, body, build: latestWhatsNew(whatsNewText) },
      (m) => setNote({ kind: "bad", text: m }),
    );
    setBusy(false);
    if (ok) { setBody(""); setNote({ kind: "ok", text: "Sent — thank you. It reaches the GM's Campaign settings." }); }
  };

  return (
    <>
      <button className="feedback-pill" onClick={show} title="Tell the GM what's wrong, or what would help">Feedback</button>
      {open && (
        <div className="modal-back topmost" onClick={close} onKeyDown={(e) => { if (e.key === "Escape") close(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Send feedback">
            <h2 className="panel-title">Feedback</h2>
            <p className="muted tiny feedback-page" title="The page this was pressed on goes with the note">{page}</p>
            <textarea ref={box} className="feedback-box" rows={6} value={body} maxLength={4000}
                      placeholder="What's wrong, or what would help?"
                      onChange={(e) => setBody(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void send(); }} />
            {note && <p className={note.kind === "ok" ? "ok-note" : "warn-note"} role="status">{note.text}</p>}
            <div className="feedback-actions">
              <button className="primary" onClick={() => void send()} disabled={busy || !body.trim()}>{busy ? "Sending…" : "Send"}</button>
              <button onClick={close}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
